'use client';

import React, { useCallback } from 'react';
import type { ReactNode } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ComponentNode, ComponentStyles } from '../types/template';
import { convertStylesToInline } from '../utils/styleUtils';
import { resolveCustomThemeHref, buildMerchantHref } from '../utils/linkHelpers';
import { getNestedValue } from '../utils/dataBinding';
import { apiOutUrl } from '@/apis/user';
import { extractOfferCodeFromUrl } from '@/constants/hooks';

function resolveTextRules(
    rules: Array<{ when: string; equals?: string; text: string; styles?: ComponentStyles }> | undefined,
    data: unknown,
): { text: string; styles?: ComponentStyles } | undefined {
    if (!rules || !rules.length) return undefined;
    for (const rule of rules) {
        const value = getNestedValue(data, rule.when);
        if (value == null || value === '') continue;
        if (rule.equals !== undefined) {
            if (String(value).trim() !== rule.equals) continue;
            return { text: rule.text, styles: rule.styles };
        }
        return { text: rule.text || String(value).trim(), styles: rule.styles };
    }
    return undefined;
}

interface LinkComponentProps {
    component: ComponentNode;
    data?: any;
    children?: ReactNode;
    context?: {
        domain?: string;
        store_slug?: string;
        slug_type?: string;
        category_slug?: string;
        promotion_slug?: string;
        out_link?: string;
    };
}

const LinkComponent: React.FC<LinkComponentProps> = ({ component, data, context, children }) => {
    const { data: componentData, placeholder, href, styles, trackOutUrl, textRules } = component;
    const inlineStyles = convertStylesToInline(styles);
    const router = useRouter();

    // `""` means "bind to the item itself" (a plain string row, not an object
    // with a named field) — only a genuinely absent placeholder skips this.
    const dynamicValue = placeholder !== undefined ? getNestedValue(data, placeholder) : undefined;
    const label =
        typeof dynamicValue === 'string'
            ? dynamicValue.trim() || componentData
            : dynamicValue ?? componentData;

    // Conditional text/style (see ComponentNode.textRules): first matching
    // rule wins — e.g. offer CTA reading "Buy Now" for product-type offers,
    // or swapping to a dashed coupon-code chip look when one exists, else
    // the node's usual label/styles.
    const matchedRule = resolveTextRules(textRules, data);
    const resolvedLabel = matchedRule?.text ?? label;
    const resolvedStyles = matchedRule?.styles
        ? { ...inlineStyles, ...convertStylesToInline(matchedRule.styles) }
        : inlineStyles;

    const resolvedHref = resolveCustomThemeHref(href, data, context) || '#';

    // Generic tracked-outbound-link flow (see ComponentNode.trackOutUrl): on
    // click, extract the offer code embedded in the resolved href, resolve
    // the real redirect URL via the out-URL tracking endpoint, then navigate.
    // `resolvedHref` stays in the DOM as the static/SEO fallback href.
    const handleTrackedClick = useCallback(
        async (e: React.MouseEvent<HTMLAnchorElement>) => {
            // The merchant-page preview tab (mirrors OfferOutUrl.tsx) is
            // independent of out-URL tracking — it only needs a resolvable
            // merchant href + unique id, not an /out/O/<hash> offer code, so
            // it must still open even for offers whose url is a raw
            // affiliate link (the tracking step below is then just skipped).
            // Best-effort: skipped when no merchant href/unique id can be
            // resolved from this item.
            const merchantHref = buildMerchantHref(data, context);
            const uniqueId = getNestedValue(data, 'offer.unique_id') ?? getNestedValue(data, 'unique_id');
            if (merchantHref && merchantHref !== '#' && uniqueId) {
                const baseUrl = window.location.origin;
                const newTabUrl = new URL(`${baseUrl}${merchantHref}`);
                newTabUrl.searchParams.set('show', 'true');
                newTabUrl.searchParams.set('p_id', String(uniqueId));
                window.open(newTabUrl.toString(), '_blank');
            }

            const domain = context?.domain;
            const offerCode = extractOfferCodeFromUrl(resolvedHref);

            if (!domain || !offerCode) {
                // Nothing to track against — the merchant tab (if any) already
                // opened above; let this click's own href navigate normally.
                return;
            }

            e.preventDefault();

            try {
                const response = await apiOutUrl(domain, offerCode);
                const redirectUrl = response?.data;
                if (redirectUrl) {
                    window.location.href = redirectUrl;
                } else {
                    router.push(resolvedHref);
                }
            } catch (error) {
                console.error('Error getting redirect URL', error);
                router.push(resolvedHref);
            }
        },
        [resolvedHref, context, data, router]
    );

    if (trackOutUrl) {
        return (
            <Link
                href={resolvedHref}
                style={resolvedStyles}
                target="_blank"
                rel="nofollow sponsored noopener noreferrer"
                onClick={handleTrackedClick}
            >
                {children ?? resolvedLabel}
            </Link>
        );
    }

    return (
        <Link href={resolvedHref} style={resolvedStyles}>
            {children ?? resolvedLabel}
        </Link>
    );
};

export default LinkComponent;
